//final latest
import React, { useEffect, useState } from "react";
import TherapyRoomCard from "./TherapyRoomCard";
import Navbar from "../Navbar";
import Footer from "../Footer";
import beach from "../../assets/images/beach.png";
import forest from "../../assets/images/forest.png";
import park from "../../assets/images/park.png";
import campsite from "../../assets/images/campsite.png";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Env from '../../runtimeEnv';

function TherapyRooms({
  setCurrentNickname,
  currentNickname,
  handleLogout,
  setFormType,
  PlutonicuserID,
}) {
  const navigate = useNavigate();
  const [enabledRoom, setEnabledRoom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roomsOnlineStatus, setRoomsOnlineStatus] = useState({});
  const [sessionStartTimes, setSessionStartTimes] = useState({});

  useEffect(() => {
    const initializeState = async () => {
      const savedUserID =
        PlutonicuserID || localStorage.getItem("PlutonicuserID");
      const savedNickname =
        currentNickname || localStorage.getItem("currentNickname");

      if (savedNickname) {
        setCurrentNickname(savedNickname);
      }

      if (savedUserID) {
        try {
          const response = await axios.get(
            `${Env.REACT_APP_PYTHON_API_URL}/api/GetMeetings/${savedUserID}`
          );

          // console.log("[TherapyRooms] API Response:", response.data);

          if (response.data.length > 0) {
            const nowEST = new Date(
              new Date().toLocaleString("en-US", {
                timeZone: "America/New_York",
              })
            );
            const gracePeriodBeforeMs = 15 * 60 * 1000; // 15 minutes before session
            const gracePeriodAfterMs = 75 * 60 * 1000; // 15 minutes after session

            // Organize the sessions
            const roomSessionsMap = response.data.reduce((acc, room) => {
              const roomId = room.meetingID.split("-")[0];
              const sessionTimeUTC = new Date(room.timeStamp); // Use UTC time
              const sessionTimeEST = new Date(
                sessionTimeUTC.toLocaleString("en-US", {
                  timeZone: "America/New_York",
                })
              );

              const timeUntilSession = sessionTimeEST - nowEST;
              const timeSinceSession = nowEST - sessionTimeEST;

              const isInGracePeriod =
                (timeUntilSession <= gracePeriodBeforeMs &&
                  timeUntilSession >= 0) || // 15 minutes before session
                (timeSinceSession >= 0 &&
                  timeSinceSession <= gracePeriodAfterMs); // 15 minutes after session

              const isUpcoming = timeUntilSession > 0; // Session is in the future

              const options = {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              };

              const formattedTimeEST =
                sessionTimeEST.toLocaleString("en-US", options) + " EST";

              if (!acc[roomId]) {
                acc[roomId] = [];
              }

              acc[roomId].push({
                meetingID: room.meetingID,
                sessionStartTimeEST: formattedTimeEST,
                sessionStartTimeUTC: sessionTimeUTC.toISOString(), // Store UTC time for backend consistency
                isAvailable: isInGracePeriod,
                isUpcoming,
                online: room.online.some((status) => status === true), // Correctly map online status
              });

              return acc;
            }, {});

            // Sort sessions for each room by session start time
            Object.keys(roomSessionsMap).forEach((roomId) => {
              roomSessionsMap[roomId].sort(
                (a, b) =>
                  new Date(a.sessionStartTimeUTC) -
                  new Date(b.sessionStartTimeUTC)
              );
            });

            // console.log("[TherapyRooms] Room Sessions Map:", roomSessionsMap);

            // Prepare session start times for only the next relevant session
            const sessionStartTimesMap = Object.entries(roomSessionsMap).reduce(
              (acc, [roomId, sessions]) => {
                // Find the next upcoming or in-grace-period session
                const nextSession = sessions.find(
                  (session) => session.isAvailable || session.isUpcoming
                );

                if (nextSession) {
                  acc[roomId] = nextSession.sessionStartTimeEST; // Only show the next session datetime
                }

                return acc;
              },
              {}
            );

            // console.log("[TherapyRooms] Session Start Times Map:", sessionStartTimesMap);

            const onlineStatusMap = Object.entries(roomSessionsMap).reduce(
              (acc, [roomId, sessions]) => {
                acc[roomId] = sessions.some((session) => session.online); // At least one session is online
                return acc;
              },
              {}
            );

            setRoomsOnlineStatus(onlineStatusMap); // Update online status
            setEnabledRoom(roomSessionsMap); // Store all sessions per room
            setSessionStartTimes(sessionStartTimesMap); // Store only the next session times for display
            
          } else {
            // console.warn(`[TherapyRooms] No available rooms for user: ${savedUserID}`);
          }
        } catch (error) {
          // console.error("[TherapyRooms] Failed to fetch meeting details:", error);
        }
      }

      setLoading(false);
    };

    initializeState();
  }, [PlutonicuserID, currentNickname, setCurrentNickname]);

  const rooms = [
    {
      id: "beach",
      title: "Plutonic Therapy Beach Office",
      description:
        "Consider having a session with your licensed mental health professional inside the office or in the cabana. Take in the sounds of the ocean waves and the sunset as you reduce stress.",
      accessibility: "PC Web Browser | Chrome, Edge",
      style: "Modern Design",
      usage: "Family Counseling",
      image: beach,
      available:
        enabledRoom["beach"]?.some((session) => session.isAvailable) || false,
      isComingSoon: false,
      online: roomsOnlineStatus["beach"] || false,
      sessionStartTimeEST: sessionStartTimes["beach"] || "",
    },
    {
      id: "mountain",
      title: "Plutonic Therapy Forest Office",
      description:
        "Consider having a session with your licensed mental health professional next to the fireplace. Take in the sounds and sights of the forest.",
      accessibility: "PC Web Browser | Chrome, Edge",
      style: "Modern Design",
      usage: "Family Counseling",
      image: forest,
      available:
        enabledRoom["mountain"]?.some((session) => session.isAvailable) ||
        false,
      isComingSoon: true,
      online: roomsOnlineStatus["mountain"] || false,
      sessionStartTimeEST: sessionStartTimes["mountain"] || "",
    },
    {
      id: "park",
      title: "Plutonic Therapy Quiet Park",
      description:
        "Consider having a session with your licensed mental health professional while taking a walk around the park or while sitting on a park bench. This quiet park is a perfect safe space to talk.",
      accessibility: "PC Web Browser | Chrome, Edge",
      style: "Green Space",
      usage: "Family Counseling",
      image: park,
      available:
        enabledRoom["park"]?.some((session) => session.isAvailable) || false,
      isComingSoon: false,
      online: roomsOnlineStatus["park"] || false,
      sessionStartTimeEST: sessionStartTimes["park"] || "",
    },
   
  ];

  const handleEnterClick = (roomType) => {
    // console.log(`[TherapyRooms] Attempting to enter room: ${roomType}`);

    // Handle test-beach and test-park explicitly
    if (roomType === "test-beach" || roomType === "test-park") {
      const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");
      const testMeetingID = `${roomType}-test-meeting-id`; // Mock meeting ID based on room type

      // console.log(`[TherapyRooms] Test room clicked: ${roomType}`);
      sessionStorage.setItem("meetingID", testMeetingID); // Set mock meeting ID
      sessionStorage.setItem("userId", userId); // Set user ID

      const realRoomType = roomType === "test-beach" ? "beach" : "park"; // Map test room to real room
      // console.log(`[TherapyRooms] Navigating to Unity build for ${realRoomType} room`);
      navigate(`/unity-room/${realRoomType}`); // Navigate to the respective Unity room
      return;
    }

    // Handle other real rooms
    const roomSessions = enabledRoom[roomType];
    if (!roomSessions || roomSessions.length === 0) {
      alert("Room is not enabled. Please wait or check your eligibility.");
      // console.log(`[TherapyRooms] No sessions available for room: ${roomType}`);
      return;
    }

    const session = roomSessions.find((s) => s.isAvailable);
    if (!session) {
      alert("No active session is available at this time.");
      // console.log(`[TherapyRooms] No active session found for room: ${roomType}`);
      return;
    }

    const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");
    sessionStorage.setItem("meetingID", session.meetingID);
    sessionStorage.setItem("userId", userId);

    // console.log(`[TherapyRooms] Navigating to /unity-room/${roomType}`);
    navigate(`/unity-room/${roomType}`);
  };

  if (loading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>Loading...</p>
      </div>
    );
  }
  

  return (
    <div>
      <Navbar
        setCurrentNickname={(nickname) => {
          setCurrentNickname(nickname);
          localStorage.setItem("currentNickname", nickname);
        }}
        currentNickname={currentNickname}
        handleLogout={handleLogout}
        setFormType={setFormType}
        showButtons={true}
        PlutonicuserID={
          PlutonicuserID || localStorage.getItem("PlutonicuserID")
        }
      />
      <div
        className="flex flex-col items-center min-h-screen pt-24 pb-16 px-12 mx-auto"
        style={{
          background: "linear-gradient(to bottom, #CBCECE, #77D8EE, #CBCECE)",
        }}
      >
        <h2 className="text-3xl font-bold mb-12 self-start pl-4 mt-8">
          Your Virtual Spaces
        </h2>
        {/* {console.log("rooms to render:", rooms.map((r) => r.id))} */}

        <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full">
          {rooms.map((room) => (
            <TherapyRoomCard
              key={room.id}
              image={room.image}
              title={room.title}
              description={room.description}
              accessibility={room.accessibility}
              style={room.style}
              usage={room.usage}
              available={room.available}
              isComingSoon={room.isComingSoon}
              online={room.online}
              sessionStartTimeEST={room.sessionStartTimeEST} // Pass session start time
              onEnterClick={() => handleEnterClick(room.id)}
            />
          ))}
            <p className="text-black text-center mt-4 col-span-full">--- End of Cards ---</p>

        </div>
        
      </div>
      
      <Footer />
    </div>
  );
}

export default TherapyRooms;
